import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Send, Mail } from 'lucide-react'
import { EmailContent } from './EmailContentModal'
import { Test } from '@/contexts/EmployeeContext'

interface EmailPreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onBack: () => void
  onSend: () => void
  content: EmailContent
  test: Test
  selectedEmployeeCount: number
}

const replacePlaceholders = (input: string, mapping: Record<string, string>) => {
  let out = input || ''
  Object.entries(mapping).forEach(([k, v]) => {
    out = out.split(`{${k}}`).join(v ?? '')
  })
  return out
}

const EmailPreviewModal = ({
  open,
  onOpenChange,
  onBack,
  onSend,
  content,
  test,
  selectedEmployeeCount,
}: EmailPreviewModalProps) => {
  if (!content) return null

  const now = new Date()
  const tr = 'tr-TR'
  const startDate = new Date(now.getTime() + 24 * 60 * 60 * 1000)
  const dueDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

  const timeLimitMin =
    typeof (test as any)?.time_limit === 'number'
      ? Math.max(1, Math.floor((test as any).time_limit / 60))
      : 30
  const mapping = {
    TEST_NAME: test.name || '',
    EMPLOYEE_NAME: 'Örnek Çalışan',
    ACCESS_CODE: 'ABC123',
    START_DATE: startDate.toLocaleString(tr),
    DUE_DATE: dueDate.toLocaleString(tr),
    TIME_LIMIT: `${timeLimitMin} dakika`,
    LOGIN_LINK: 'https://mavi.novaforge.tech/video-interview',
  }

  const subjectPreview = replacePlaceholders(content.subject || '', mapping)
  const bodyTextPreview = replacePlaceholders(content.body || content.content || '', mapping)
  const htmlPreview = content.html ? replacePlaceholders(content.html, mapping) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Önizleme
          </DialogTitle>
          <DialogDescription>
            {selectedEmployeeCount} çalışana gönderilecek e-postanın önizlemesi
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-yellow-50 p-3 rounded-md border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Önizleme:</strong> Placeholder’lar gerçek gönderimde her çalışan için dinamik
              olarak doldurulacaktır.
            </p>
          </div>

          <Card className="border-2">
            <CardHeader className="bg-gray-50">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Konu:</span>
                  <Badge variant="outline">{selectedEmployeeCount} alıcı</Badge>
                </div>
                <CardTitle className="text-lg">{subjectPreview}</CardTitle>
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              {/* HTML varsa render et; yoksa text göster */}
              {htmlPreview ? (
                <div className="border rounded overflow-hidden">
                  <iframe
                    title="email-html-preview"
                    className="w-full h-[520px] bg-white"
                    srcDoc={htmlPreview}
                  />
                </div>
              ) : (
                <div className="space-y-4 text-sm">
                  <div className="whitespace-pre-wrap leading-relaxed">{bodyTextPreview}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri Dön
          </Button>
          <Button onClick={onSend} className="bg-green-600 hover:bg-green-700">
            <Send className="h-4 w-4 mr-2" />
            Email Gönder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EmailPreviewModal
